import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Mic, MicOff, PhoneOff, Volume2, MessageSquare, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { resolveFirstName } from "../lib/firstName";

/**
 * Voice-first "phone call" coach interface.
 * - Auto-starts microphone on mount (after a single permission tap on iOS/Safari).
 * - Auto-listens with silence detection (RMS-based VAD).
 * - Sends to /voice/stt -> /chat/send -> /voice/tts -> back to listening.
 * - Big pulsing orb, live transcript caption, prominent red end-call button.
 */

// Silence detection thresholds (tuned for normal indoor speech).
const SILENCE_RMS = 0.012;          // RMS below this = silence
const MIN_SPEECH_MS = 350;           // require at least this much voiced audio
const SILENCE_HANG_MS = 1400;        // wait this long of silence before ending utterance
const MAX_UTTERANCE_MS = 30000;      // hard cap per utterance

// Phases of the call
const PHASE = {
  IDLE: "idle",            // not started yet (waiting for user tap to grant mic)
  LISTENING: "listening",  // mic open, waiting for / capturing speech
  THINKING: "thinking",    // STT + chat in flight
  SPEAKING: "speaking",    // TTS playing
  ENDED: "ended",
};

export default function VoiceCoachPage() {
  const { user, onboarding } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState(PHASE.IDLE);
  const [muted, setMuted] = useState(false);
  const [level, setLevel] = useState(0); // 0..1 mic input level for orb pulse
  const [caption, setCaption] = useState("");
  const [coachLine, setCoachLine] = useState("");
  const [transcript, setTranscript] = useState([]); // {role, content}[]
  const [sessionId, setSessionId] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);

  // refs that survive renders
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const speechStartRef = useRef(0);
  const lastVoiceRef = useRef(0);
  const utteranceStartRef = useRef(0);
  const audioElRef = useRef(null);
  const phaseRef = useRef(PHASE.IDLE);
  const mutedRef = useRef(false);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const firstName = onboarding?.preferred_name || resolveFirstName(user, "there");
  const coachStyle = onboarding?.coach_style || "calm";

  // --------- core lifecycle ---------
  const endCall = useCallback(() => {
    setPhase(PHASE.ENDED);
    cancelAnimationFrame(rafRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch (e) {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) {}
    try { audioCtxRef.current?.close(); } catch (e) {}
    try {
      if (audioElRef.current) {
        audioElRef.current.pause();
        audioElRef.current.src = "";
      }
    } catch (e) {}
    streamRef.current = null;
    audioCtxRef.current = null;
    analyserRef.current = null;
    recorderRef.current = null;
    setTimeout(() => navigate("/dashboard"), 200);
  }, [navigate]);

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Greeting — speak first so user knows the line is open.
      const greetings = {
        calm: `Hi ${firstName}. I'm here. Take a breath — what's on your mind?`,
        hard: `${firstName}. I'm listening. What have you been avoiding?`,
        nobs: `${firstName}, talk to me straight. What do you need to deal with right now?`,
        friend: `Hey ${firstName}! Glad you called. How are you really doing?`,
      };
      const greeting = greetings[coachStyle] || greetings.calm;
      setTranscript([{ role: "assistant", content: greeting }]);
      setCoachLine(greeting);
      await speak(greeting); // sets phase to SPEAKING then back to LISTENING
    } catch (e) {
      console.error(e);
      toast.error("Microphone access is required for voice coaching.");
      setPhase(PHASE.IDLE);
    }
  };

  // ------ TTS ------
  const speak = async (text) => {
    setPhase(PHASE.SPEAKING);
    setCoachLine(text);
    setCaption("");
    try {
      const token = localStorage.getItem("ascend_session_token");
      const resp = await fetch(`${API}/voice/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) {
        // 402 = voice not in plan. Bounce to pricing instead of looping silently.
        if (resp.status === 402) {
          toast.error("Voice coaching is in Advanced. Redirecting to plans…");
          setTimeout(() => navigate("/pricing"), 1200);
          setPhase(PHASE.ENDED);
          return;
        }
        throw new Error("TTS failed");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = audioElRef.current;
      a.src = url;
      a.onended = () => {
        URL.revokeObjectURL(url);
        if (phaseRef.current !== PHASE.ENDED) startListening();
      };
      await a.play();
    } catch (e) {
      console.error(e);
      // fall through to listening so the call doesn't die silently
      if (phaseRef.current !== PHASE.ENDED) startListening();
    }
  };

  // ------ Listening + VAD ------
  const startListening = () => {
    if (phaseRef.current === PHASE.ENDED) return;
    setPhase(PHASE.LISTENING);
    setCaption("");

    const stream = streamRef.current;
    if (!stream) return;

    const mr = new MediaRecorder(stream, { mimeType: getMimeType() });
    chunksRef.current = [];
    mr.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
    mr.onstop = handleRecordingStop;
    mr.start(100); // small timeslice so chunks accumulate
    recorderRef.current = mr;

    speechStartRef.current = 0;
    lastVoiceRef.current = 0;
    utteranceStartRef.current = performance.now();

    monitorLoop();
  };

  const monitorLoop = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.frequencyBinCount);

    const step = () => {
      if (phaseRef.current !== PHASE.LISTENING) return;
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      // visual level (smoothed)
      setLevel((prev) => prev * 0.7 + Math.min(1, rms * 8) * 0.3);

      const now = performance.now();
      const speaking = !mutedRef.current && rms > SILENCE_RMS;

      if (speaking) {
        if (!speechStartRef.current) speechStartRef.current = now;
        lastVoiceRef.current = now;
      }

      const utteranceMs = now - utteranceStartRef.current;
      const silenceMs = lastVoiceRef.current ? now - lastVoiceRef.current : 0;
      const speechMs = speechStartRef.current ? lastVoiceRef.current - speechStartRef.current : 0;

      // End conditions:
      // 1) had real speech, then went silent for SILENCE_HANG_MS
      // 2) hit max utterance length
      const hadSpeech = speechMs >= MIN_SPEECH_MS;
      if ((hadSpeech && silenceMs >= SILENCE_HANG_MS) || utteranceMs >= MAX_UTTERANCE_MS) {
        try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch (e) {}
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const handleRecordingStop = async () => {
    if (phaseRef.current === PHASE.ENDED) return;
    const hadSpeech = !!speechStartRef.current;
    if (!hadSpeech) {
      // No speech detected — just resume listening silently
      if (phaseRef.current !== PHASE.ENDED) startListening();
      return;
    }
    setPhase(PHASE.THINKING);
    const blobType = chunksRef.current[0]?.type || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: blobType });
    chunksRef.current = [];

    try {
      // STT — let axios auto-set multipart/form-data with the right boundary.
      // Manually setting Content-Type without a boundary breaks the upload.
      // Filename extension matches the actual blob type so iOS-mp4 audio doesn't
      // get rejected by servers that sniff the extension.
      const ext = blobType.includes("mp4") ? "m4a"
        : blobType.includes("ogg") ? "ogg"
        : "webm";
      const form = new FormData();
      form.append("audio", blob, `speech.${ext}`);
      const sttResp = await axios.post(`${API}/voice/stt`, form);
      const userText = (sttResp.data.text || sttResp.data.transcript || "").trim();
      if (!userText) {
        // No words detected — show a hint, don't hang in silent listening
        setCaption("I didn't catch that — try again.");
        if (phaseRef.current !== PHASE.ENDED) startListening();
        return;
      }
      setTranscript((t) => [...t, { role: "user", content: userText }]);
      setCaption(userText);

      // CHAT
      const chatResp = await axios.post(`${API}/chat/send`, {
        message: userText,
        session_id: sessionId,
      });
      setSessionId(chatResp.data.session_id);
      const reply = chatResp.data.reply;
      const actions = chatResp.data.actions || [];
      actions.forEach((a) => {
        if (a.type === "add_milestone") toast.success(`Coach added: ${a.milestone?.title || "new milestone"}`);
        else if (a.type === "move_milestone") toast.success("Coach moved a milestone.");
        else if (a.type === "complete_milestone") toast.success("Coach marked a milestone complete.");
        else if (a.type === "delete_milestone") toast.success("Coach removed a milestone.");
      });
      setTranscript((t) => [...t, { role: "assistant", content: reply }]);
      setCoachLine(reply);

      if (phaseRef.current !== PHASE.ENDED) await speak(reply);
    } catch (e) {
      console.error("Voice flow error:", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      let userFacing;
      if (status === 402) {
        userFacing = "Your trial has ended. Tap End Call and choose a plan to keep talking.";
      } else if (status === 429) {
        userFacing = "Daily message limit reached. Try again tomorrow or upgrade.";
      } else if (status === 401) {
        userFacing = "You're signed out. End the call and log back in.";
      } else if (status >= 500) {
        userFacing = "The coach is having a network blip. Try once more.";
      } else if (typeof detail === "string") {
        userFacing = detail;
      } else {
        userFacing = "I lost the line for a second. Try once more.";
      }

      setCoachLine(userFacing);
      setCaption(userFacing);
      toast.error(userFacing, { duration: 6000 });
      if (phaseRef.current !== PHASE.ENDED) startListening();
    }
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch (e) {}
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) {}
      try { audioCtxRef.current?.close(); } catch (e) {}
    };
  }, []);

  // ---------- UI ----------
  const orbScale = 1 + (phase === PHASE.SPEAKING ? 0.15 : level * 0.6);
  const ring1 = 1 + (phase === PHASE.LISTENING ? level * 0.4 : 0);
  const ring2 = 1 + (phase === PHASE.LISTENING ? level * 0.7 : 0);

  const styleTag = {
    calm: "Calm & Collected",
    hard: "Hard & Straight",
    nobs: "No-BS Mentor",
    friend: "Best Friend",
  }[coachStyle] || "Your Coach";

  const statusText = {
    [PHASE.IDLE]: "Tap to start the call",
    [PHASE.LISTENING]: muted ? "Muted — tap mic to unmute" : "Listening…",
    [PHASE.THINKING]: "Thinking…",
    [PHASE.SPEAKING]: "Coach is speaking",
    [PHASE.ENDED]: "Call ended",
  }[phase];

  return (
    <div
      data-testid="voice-coach-page"
      className="min-h-screen flex flex-col text-white relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #1c2944 0%, #0f1724 45%, #050912 100%)",
      }}
    >
      {/* subtle grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Header */}
      <header className="relative z-10 px-6 pt-6 pb-2 flex items-center justify-between max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-2">
          <span className="font-display text-[18px] font-semibold">ascend</span>
          <span className="font-display text-[18px] font-semibold text-[#8aa7c4]">higher</span>
        </div>
        <button
          data-testid="voice-coach-text-mode-btn"
          onClick={() => navigate("/coach")}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 font-body text-[12px] transition-colors"
        >
          <MessageSquare size={13} /> Switch to text
        </button>
      </header>

      {/* Coach identity */}
      <div className="relative z-10 text-center mt-4 px-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15">
          <Sparkles size={11} className="text-[#8aa7c4]" />
          <span className="font-mono-eyebrow text-[10px] tracking-[0.16em] text-[#8aa7c4]">
            {styleTag.toUpperCase()}
          </span>
        </div>
        <p className="font-body text-[14px] text-white/70 mt-3">
          {phase === PHASE.IDLE ? `Hi ${firstName}` : `On a call with your coach`}
        </p>
      </div>

      {/* Orb */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        <div className="relative w-[260px] h-[260px] md:w-[320px] md:h-[320px] flex items-center justify-center">
          {/* outer ring */}
          <div
            className="absolute inset-0 rounded-full border border-white/10 transition-transform duration-150 ease-out"
            style={{
              transform: `scale(${ring2.toFixed(3)})`,
              opacity: phase === PHASE.LISTENING ? 0.6 : 0.2,
            }}
          />
          <div
            className="absolute inset-6 rounded-full border border-white/15 transition-transform duration-150 ease-out"
            style={{
              transform: `scale(${ring1.toFixed(3)})`,
              opacity: phase === PHASE.LISTENING ? 0.7 : 0.25,
            }}
          />
          {/* speaking pulse */}
          {phase === PHASE.SPEAKING && (
            <div className="absolute inset-0 rounded-full animate-ping bg-[#8aa7c4]/10" />
          )}
          {/* core orb */}
          <div
            className="relative w-[180px] h-[180px] md:w-[220px] md:h-[220px] rounded-full transition-transform duration-200 ease-out"
            style={{
              transform: `scale(${orbScale.toFixed(3)})`,
              background:
                "radial-gradient(circle at 35% 30%, #c7d6e8 0%, #6f8caf 35%, #486a8f 65%, #1c2944 100%)",
              boxShadow:
                "0 0 80px rgba(72,106,143,0.55), inset 0 0 60px rgba(255,255,255,0.15)",
            }}
          >
            {phase === PHASE.THINKING && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center">
                <Loader2 size={42} className="text-white/85 animate-spin" />
              </div>
            )}
            {phase === PHASE.SPEAKING && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center">
                <Volume2 size={42} className="text-white/90" />
              </div>
            )}
          </div>
        </div>

        {/* Status */}
        <p
          data-testid="voice-coach-status"
          className="mt-8 font-mono-eyebrow text-[11px] tracking-[0.18em] text-[#8aa7c4]"
        >
          {statusText.toUpperCase()}
        </p>

        {/* Live caption */}
        <div className="mt-5 max-w-xl text-center min-h-[60px]">
          {phase === PHASE.SPEAKING && coachLine && (
            <p className="font-body text-[16px] md:text-[18px] text-white/90 leading-relaxed">
              {coachLine.length > 220 ? coachLine.slice(0, 220) + "…" : coachLine}
            </p>
          )}
          {phase === PHASE.THINKING && caption && (
            <p className="font-body text-[14px] text-white/55 italic">"{caption}"</p>
          )}
          {phase === PHASE.LISTENING && caption && (
            <p className="font-body text-[14px] text-amber-200/85 leading-relaxed">
              {caption}
            </p>
          )}
          {phase === PHASE.IDLE && (
            <p className="font-body text-[14px] md:text-[15px] text-white/75 leading-relaxed max-w-md mx-auto">
              <span className="text-white font-medium">Talk for 2 minutes.</span> Your coach will turn it into today's plan.
              <br />
              <span className="text-white/55 text-[13px]">Speak naturally — pauses are how they know you're done.</span>
            </p>
          )}
        </div>
      </main>

      {/* Controls */}
      <footer className="relative z-10 pb-10 pt-2 px-6">
        <div className="max-w-md mx-auto flex items-center justify-center gap-6">
          {phase === PHASE.IDLE ? (
            <button
              data-testid="voice-coach-start-btn"
              onClick={startCall}
              className="h-16 px-10 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white font-medium text-[16px] inline-flex items-center gap-3 shadow-[0_10px_40px_-10px_rgba(62,107,82,0.7)] btn-lift"
            >
              <Mic size={20} />
              Start the call
            </button>
          ) : (
            <>
              {/* Mute */}
              <button
                data-testid="voice-coach-mute-btn"
                onClick={() => setMuted((m) => !m)}
                disabled={phase === PHASE.ENDED}
                className={`w-14 h-14 rounded-full inline-flex items-center justify-center transition-colors border ${
                  muted
                    ? "bg-white/15 border-white/25 text-white"
                    : "bg-white/8 border-white/15 text-white/85 hover:bg-white/12"
                }`}
                title={muted ? "Unmute" : "Mute"}
              >
                {muted ? <MicOff size={20} /> : <Mic size={20} />}
              </button>

              {/* End call */}
              <button
                data-testid="voice-coach-end-btn"
                onClick={endCall}
                className="w-20 h-20 rounded-full bg-[#b94e4e] hover:bg-[#a33f3f] active:scale-95 text-white inline-flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(185,78,78,0.85)] transition-transform"
                title="End call"
              >
                <PhoneOff size={28} />
              </button>

              {/* Toggle transcript */}
              <button
                data-testid="voice-coach-transcript-btn"
                onClick={() => setShowTranscript((s) => !s)}
                className="w-14 h-14 rounded-full inline-flex items-center justify-center bg-white/8 hover:bg-white/12 border border-white/15 text-white/85 transition-colors"
                title="Show transcript"
              >
                <MessageSquare size={20} />
              </button>
            </>
          )}
        </div>

        {/* Hint line */}
        <p className="text-center font-body text-[11px] text-white/45 mt-6">
          {phase === PHASE.IDLE
            ? "We need mic access for voice. Nothing is recorded after the call."
            : "Pause for a moment when you're done — your coach will respond."}
        </p>
      </footer>

      {/* Transcript drawer */}
      {showTranscript && (
        <div
          data-testid="voice-coach-transcript-panel"
          className="absolute inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setShowTranscript(false)}
        >
          <div
            className="w-full max-w-lg max-h-[70vh] overflow-y-auto rounded-2xl bg-[#0f1724] border border-white/10 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-mono-eyebrow text-[10px] tracking-[0.16em] text-[#8aa7c4]">TRANSCRIPT</p>
            {transcript.length === 0 && (
              <p className="font-body text-[13px] text-white/55">Nothing said yet.</p>
            )}
            {transcript.map((m, i) => (
              <div key={i}>
                <p className="font-mono-eyebrow text-[9px] tracking-[0.16em] text-white/40 mb-1">
                  {m.role === "user" ? "YOU" : "COACH"}
                </p>
                <p className="font-body text-[14px] leading-relaxed text-white/90 whitespace-pre-wrap">
                  {m.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <audio ref={audioElRef} hidden />
    </div>
  );
}

function getMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return "";
}
